import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import {
  useDialogStore,
  useDispatch,
  useLanguage,
  useLocaleFormat,
  useRootStore,
  useTranslation,
} from "../stores/RootStoreContext";
import {
  AddCompaniesModel,
  EnterpriseAccountsStore,
  formatAccountStatus,
  formatLink,
  productNameLabel,
} from "./enterpriseAccountsDomain";
import { LoadingIndicator } from "../components-v2/LoadingIndicator";
import { Box, Card, Container, VBox } from "../components-v2/Box";
import {
  AccountCompanyDetails,
  EnterpriseAccountDetails,
  EnterpriseAccountStatus,
  isFailure,
  isOk,
  isPending,
  productNames,
} from "../common/common";
import { Icon } from "../components-v2/Icon";
import { FullScreenLayout } from "../components-v2/FullScreenLayout";
import { Button, IconButton, LinkButton } from "../components-v2/Button";
import { loadUrl, navigate } from "../events/eventsDomain";
import { superadminSearchUrl, superadminUrl } from "../utils/navigation";
import { InfoTag } from "../components-v2/InfoTag";
import { TabProps, Tabs } from "../components-v2/Tabs";
import { DataTable, TableColumn } from "../components-v2/DataTable";
import { formatOrgNum } from "../utils/other";
import { MTextInput } from "../components-v2/TextInput";
import { MSelect, Select } from "../components-v2/Select";
import { Dialog } from "../components-v2/Dialog";
import { MTextArea } from "../components-v2/TextArea";
import { Integrations } from "./integrationsComponents";
import AlertMessageStore from "../stores/AlertMessageStore";
import { Table } from "../components-v2/Table";
import { getFullName } from "../utils/Roles";
import { formatDate } from "../i18n/dateFormat";
import { identity } from "lodash";
import { isEnter } from "../utils/keyboardEvents";
import { Checkbox } from "../components-v2/Checkbox";

export const EnterpriseAccountPage = observer(function EnterpriseAccountPage({
  id,
}: {
  id: string;
}) {
  const { http, messageStore, dispatch } = useRootStore();
  const [store] = useState(new EnterpriseAccountsStore(http, messageStore, dispatch, id));
  const { t } = useTranslation();
  useEffect(() => {
    store.fetchEnterpriseAccount();
  }, [store]);

  switch (true) {
    case isPending(store.accountResult):
      return (
        <VBox fullheight justifyContent={"center"}>
          <LoadingIndicator />
        </VBox>
      );
    case isOk(store.accountResult):
      return (
        <EnterpriseAccountView
          data={store.accountResult.data}
          store={store}
          messageStore={messageStore}
        />
      );
    case isFailure(store.accountResult):
      return (
        <VBox fullheight justifyContent={"center"} alignItems={"center"}>
          <Icon name={"warning-circle"} color={"error"} size={"jumbo"} />
          <Box textSize={"lg"} fg={"error"}>
            {t("error.fetch.data")}
          </Box>
        </VBox>
      );
    default:
      return null;
  }
});

const EnterpriseAccountView = observer(function EnterpriseAccountView({
  data,
  store,
  messageStore,
}: {
  data: EnterpriseAccountDetails;
  store: EnterpriseAccountsStore;
  messageStore: AlertMessageStore;
}) {
  const render = () => {
    switch (store.activeTab) {
      case "companies":
        return data.companyDetails.length === 0 ? (
          <EmptyState store={store} />
        ) : (
          <CompaniesTable store={store} />
        );
      case "account":
        return <AccountDetailsView store={store} data={data} />;
      case "integrations":
        return <Integrations store={store} />;
    }
  };
  return (
    <FullScreenLayout
      pageHeader={<PageHeader store={store} data={data} tabs={store.tabs} />}
      centerPanel={
        <>
          {render()}
          {store.addCompaniesModel && (
            <AddCompaniesDialog
              model={store.addCompaniesModel}
              onCancel={store.hideAddCompaniesDialog}
              onSubmit={store.submitAddCompaniesDialog}
              isSubmitting={isPending(store.submitCompaniesResult)}
            />
          )}
        </>
      }
      alertStore={messageStore}
    />
  );
});

interface EmptyStateProps {
  store: EnterpriseAccountsStore;
}

const EmptyState = observer(function EmptyState({ store }: EmptyStateProps) {
  const { t } = useTranslation();
  return (
    <Container
      marginTop={"md"}
      maxWidth={"xl"}
      justifyContent={"center"}
      alignItems={"center"}
      fullheight
      gap={"lg"}
    >
      <h3>{t("enterprise-account.no-companies")}</h3>
      <Button
        variant={"secondary"}
        onClick={store.showAddCompaniesDialog}
        icon={"plus"}
        label={{ key: "enterprise-account.add-companies" }}
      />
    </Container>
  );
});

const PageHeader = observer(function PageHeader({
  data,
  tabs,
  store,
}: {
  data: EnterpriseAccountDetails;
  tabs: TabProps[];
  store: EnterpriseAccountsStore;
}) {
  const { t } = useTranslation();
  const dialogStore = useDialogStore();
  const { formatDate } = useLocaleFormat();
  const lang = useLanguage();
  const hubspotLink = data.account.metadata.hubspotLink;
  const tripletexLink = data.account.metadata.tripletexLink;
  const keyAccountManager = data.account.metadata.keyAccountManager;
  return (
    <VBox bg={"default"}>
      <Box grow alignItems={"center"} paddingLeft={"md"} paddingRight={"lg"} paddingY={"md"}>
        <IconButton
          icon={"x"}
          tooltip={{ key: "back" }}
          onClick={[navigate(`${superadminUrl}/enterprise-accounts`)]}
        />
        {t("enterprise-account.manage")}
      </Box>
      <Container maxWidth={"xl"}>
        <Box>
          <VBox gap={"md"}>
            <h3>{data.account.name}</h3>
            <Box>
              <InfoTag
                id={"status"}
                icon={
                  data.account.status === EnterpriseAccountStatus.ACTIVE
                    ? { name: "circle", color: "success", weight: "fill", size: "xs" }
                    : "circle-notch"
                }
                text={{
                  key:
                    data.account.status === EnterpriseAccountStatus.ACTIVE
                      ? "enterprise-account.status.active"
                      : "enterprise-account.status.inactive",
                }}
              />
              <InfoTag
                id={"startTime"}
                icon={"handshake"}
                text={formatDate(data.account.startTime)}
              />
              <InfoTag
                id={"endTime"}
                icon={"calendar-check"}
                text={
                  data.account.endTime
                    ? formatDate(data.account.endTime)
                    : t("enterprise-account.no-end-date")
                }
              />
              <InfoTag
                id={"shareholderRegister"}
                tooltip={{ key: "enterprise-account.shareholder-register" }}
                icon={"notebook"}
                text={formatAccountStatus(lang, data, "shareholder_register")}
              />
              <InfoTag
                id={"digitalCompany"}
                tooltip={{ key: "enterprise-account.digital-company" }}
                icon={"sparkle"}
                text={formatAccountStatus(lang, data, "digital_company")}
              />
              {keyAccountManager && (
                <InfoTag
                  id={"keyAccountManager"}
                  tooltip={{ key: "enterprise-account.key-account-manager" }}
                  text={keyAccountManager}
                  icon={"user-square"}
                />
              )}
              {hubspotLink && (
                <Button
                  variant={"secondary"}
                  icon={"arrow-square-out"}
                  onClick={() => window.open(formatLink(hubspotLink))}
                  label={"Hubspot"}
                  size={"xs"}
                />
              )}
              {tripletexLink && (
                <Button
                  variant={"secondary"}
                  icon={"arrow-square-out"}
                  onClick={() => window.open(formatLink(tripletexLink))}
                  label={"Tripletex"}
                  size={"xs"}
                />
              )}
            </Box>
          </VBox>
          <Box grow justifyContent={"end"} alignItems={"center"}>
            <Button
              variant={"secondary"}
              label={{ key: "enterprise-account.edit-details" }}
              icon={"edit"}
              onClick={() =>
                dialogStore.openEnterpriseAccountDialog(store.fetchEnterpriseAccount, data.account)
              }
            />
          </Box>
        </Box>
        <Tabs tabs={tabs} scrollable={false} />
      </Container>
    </VBox>
  );
});

const CompaniesTable = observer(function CompaniesTable({
  store,
}: {
  store: EnterpriseAccountsStore;
}) {
  const dispatch = useDispatch();
  const { formatNumber } = useLocaleFormat();
  const columns: TableColumn<AccountCompanyDetails>[] = [
    {
      name: "companyName",
      label: { key: "name" },
      align: "left",
    },
    {
      name: "orgNo",
      label: { key: "org-no" },
      align: "left",
      getValue: (row) => formatOrgNum(row.orgNo),
    },
    {
      name: "shareholderCount",
      label: { key: "enterprise-account.number-of-shareholders" },
      getValue: (row) => formatNumber(row.shareholderCount),
    },
    {
      name: "subscription",
      label: { key: "subscription" },
      align: "left",
      getValue: (row) => (
        <Select
          value={row.productName ?? null}
          items={[...productNames, null]}
          onSelect={(value) => {
            store.updateSubscription(row, value);
          }}
          idFn={identity}
          labelFn={productNameLabel}
        />
      ),
    },
  ];
  return (
    <Container marginTop={"md"} maxWidth={"xl"}>
      <Box>
        <MTextInput
          model={store.nameFilter}
          placeholder={{ key: "search" }}
          icon={"magnifying-glass"}
          type={"search"}
          inline
        />
        {store.subscriptionTypeFilter && (
          <Box>
            <MSelect model={store.subscriptionTypeFilter} />
          </Box>
        )}
        <Box grow justifyContent={"end"}>
          <Button
            variant={"secondary"}
            onClick={store.showAddCompaniesDialog}
            icon={"plus"}
            label={{ key: "enterprise-account.add-companies" }}
          />
        </Box>
      </Box>
      <DataTable
        tableColumns={columns}
        onRowClick={(row) => dispatch([loadUrl(superadminSearchUrl(row.orgNo))])}
        tableData={store.companyData}
        getId={(row) => row.orgNo}
        rowActions={(a) => [
          [
            "context-menu",
            [
              {
                type: "item",
                label: { key: "enterprise-accounts.remove-from-account" },
                icon: "trash",
                onSelect: () => store.removeFromAccount(a.orgNo),
              },
            ],
          ],
        ]}
      />
    </Container>
  );
});

const AddCompaniesDialog = observer(function AddCompaniesDialog({
  model,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  model: AddCompaniesModel;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      title={{ key: "enterprise-account.add-companies" }}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      maxWidth={"sm"}
    >
      <VBox gap={"lg"}>
        <Box alignItems={"end"}>
          <MTextInput
            model={model.searchField}
            placeholder={{ key: "enterprise-account.search-placeholder" }}
            icon={"magnifying-glass"}
            type={"search"}
            onKeyDown={(e) => {
              if (isEnter(e)) {
                e.preventDefault();
                model.search();
              }
            }}
          />
          <Button
            variant={"primary"}
            onClick={model.search}
            icon={"magnifying-glass"}
            loading={isPending(model.searchResult)}
            tooltip={{ key: "search" }}
          />
        </Box>
        {isOk(model.searchResult) && (
          <VBox>
            <Box>
              {t("enterprise-account.users-found", { count: model.searchResult.data.length })}
            </Box>
            <VBox>
              {model.searchResult.data.map((d) => (
                <Box key={d.externalId}>
                  {d.userId}{" "}
                  <LinkButton
                    label={d.name}
                    onClick={() => model.fetchUserCompanies(d.externalId)}
                  />
                  ({d.email})
                </Box>
              ))}
            </VBox>
          </VBox>
        )}
        <MTextArea model={model.orgNos} placeholder={t("enterprise-account.paste-orgnos")} />
        <MSelect model={model.subscriptionModel} label={t("enterprise-account.subscription")} />
        <Checkbox
          label={{ key: "enterprise-account.complete-onboarding" }}
          checked={model.completeOnboarding.value}
          onChange={(e) => model.completeOnboarding.setValue(e.target.checked)}
        />
      </VBox>
    </Dialog>
  );
});

const AccountDetailsView = observer(function DetailsView({
  data,
  store,
}: {
  data: EnterpriseAccountDetails;
  store: EnterpriseAccountsStore;
}) {
  const { t, lang } = useTranslation();
  return (
    <Container marginTop={"md"} maxWidth={"lg"}>
      <Card title={{ key: "enterprise-account.details" }}>
        <VBox maxWidth={"md"}>
          <Table variant={"IN_CARD"}>
            <tbody>
              <tr>
                <td>{t("enterprise-account.account-name")}</td>
                <td>{data.account.name}</td>
              </tr>
              <tr>
                <td>{t("enterprise-account.contact-name")}</td>
                <td>{getFullName(data.account.personInfo)}</td>
              </tr>
              <tr>
                <td>{t("enterprise-account.contact-email")}</td>
                <td>
                  {data.account.personInfo.email && (
                    <a href={`mailto:${data.account.personInfo.email}`}>
                      {data.account.personInfo.email}
                    </a>
                  )}
                </td>
              </tr>
              <tr>
                <td>{t("enterprise-account.primary-company")}</td>
                <td>{data.account.primaryCompanyOrgNo}</td>
              </tr>
              {data.account.metadata.keyAccountManager && (
                <tr>
                  <td>{t("enterprise-account.key-account-manager")}</td>
                  <td>{data.account.metadata.keyAccountManager}</td>
                </tr>
              )}
              {data.account.metadata.hubspotLink && (
                <tr>
                  <td>{t("enterprise-account.hubspot")}</td>
                  <td>
                    <a href={formatLink(data.account.metadata.hubspotLink)} target={"_blank"}>
                      {formatLink(data.account.metadata.hubspotLink)}
                    </a>
                  </td>
                </tr>
              )}
              {data.account.metadata.tripletexLink && (
                <tr>
                  <td>{t("enterprise-account.tripletex")}</td>
                  <td>
                    <a href={formatLink(data.account.metadata.tripletexLink)} target={"_blank"}>
                      {formatLink(data.account.metadata.tripletexLink)}
                    </a>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </VBox>
      </Card>
      <Card title={{ key: "enterprise-account.status" }}>
        <VBox maxWidth={"md"}>
          <Table>
            <tbody>
              <tr>
                <td>{t("enterprise-account.shareholder-register")}</td>
                <td>{formatAccountStatus(lang, data, "shareholder_register")}</td>
              </tr>
              <tr>
                <td>{t("enterprise-account.digital-company")}</td>
                <td>{formatAccountStatus(lang, data, "digital_company")}</td>
              </tr>
              <tr>
                <td>{t("enterprise-account.end-date")}</td>
                <td>
                  {data.account.endTime
                    ? formatDate(lang, data.account.endTime)
                    : t("enterprise-account.no-end-date")}
                </td>
              </tr>
            </tbody>
          </Table>
        </VBox>
      </Card>
      <Card title={{ key: "enterprise-account.danger-zone" }}>
        <Box>
          <Button
            variant={"secondary"}
            color={"error"}
            label={{ key: "enterprise-account.remove-all-companies" }}
            onClick={store.openRemoveAllCompaniesDialog}
          />
          <Button
            variant={"secondary"}
            color={"error"}
            label={{
              key: "enterprise-account.delete-account",
              params: {
                name: data.account.name,
              },
            }}
            onClick={store.openDeleteAccountDialog}
          />
        </Box>
      </Card>
      {store.showDialog === "remove-all-companies" && (
        <Dialog
          title={{ key: "enterprise-account.remove-all-companies" }}
          onSubmit={store.removeAllCompanies}
          onCancel={store.closeDialog}
          submitLabel={{ key: "enterprise-account.remove-all-companies" }}
          submitColor={"error"}
        >
          {t("enterprise-account.confirm-remove-all-companies")}
        </Dialog>
      )}
      {store.showDialog === "delete-account" && (
        <Dialog
          title={{ key: "enterprise-account.delete-account", params: { name: data.account.name } }}
          onSubmit={store.deleteAccount}
          onCancel={store.closeDialog}
          submitLabel={{
            key: "enterprise-account.delete-account",
            params: { name: data.account.name },
          }}
          submitColor={"error"}
        >
          {t("enterprise-account.confirm-delete-account")}
        </Dialog>
      )}
    </Container>
  );
});
